const BASE = ''

export interface ChatCompletionRequest {
  model: string
  messages: { role: string; content: string }[]
  stream?: boolean
}

export async function fetchConfig() {
  const res = await fetch(`${BASE}/api/config`)
  if (!res.ok) throw new Error('Config fetch failed')
  return res.json()
}

export async function fetchModels() {
  const res = await fetch(`${BASE}/api/models`)
  if (!res.ok) throw new Error('Models fetch failed')
  return res.json()
}

export async function fetchChats() {
  const res = await fetch(`${BASE}/api/v1/chats/`)
  if (!res.ok) return []
  return res.json()
}

export async function createChatOnServer(chat: {
  id: string
  title: string
  messages: { role: string; content: string }[]
}): Promise<{ id: string } | null> {
  try {
    const res = await fetch(`${BASE}/api/v1/chats/new`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ chat }),
    })
    if (!res.ok) return null
    return res.json()
  } catch {
    return null
  }
}

export async function saveChatOnServer(
  id: string,
  chat: { title: string; messages: { role: string; content: string }[] }
): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/api/v1/chats/${id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ chat }),
    })
    return res.ok
  } catch {
    return false
  }
}

export async function deleteChatOnServer(id: string): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/api/v1/chats/${id}`, { method: 'DELETE' })
    return res.ok
  } catch {
    return false
  }
}

export type StreamCallback = (content: string, done: boolean) => void

export async function streamChatCompletion(
  model: string,
  messages: { role: string; content: string }[],
  onToken: StreamCallback,
  signal?: AbortSignal
): Promise<string> {
  const res = await fetch(`${BASE}/api/chat/completions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ model, messages, stream: true }),
    signal,
  })

  if (!res.ok) throw new Error(`Chat API error: ${res.status}`)

  const reader = res.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let fullContent = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() || ''

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue
      const data = line.slice(6).trim()
      if (data === '[DONE]') break
      try {
        const json = JSON.parse(data)
        const content = json.choices?.[0]?.delta?.content
        if (content) {
          fullContent += content
          onToken(fullContent, false)
        }
      } catch { /* skip malformed */ }
    }
  }

  onToken(fullContent, true)
  return fullContent
}

export async function generateImage(
  prompt: string,
  model: string = 'lustify-v8'
): Promise<string | null> {
  try {
    const res = await fetch(`${BASE}/api/v1/images/generations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model, prompt, width: 1024, height: 1024 }),
    })
    if (!res.ok) return null
    const data = await res.json()
    return data?.data?.[0]?.url || data?.images?.[0] || null
  } catch {
    return null
  }
}
